"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.loopWithTimeoutOnCond = loopWithTimeoutOnCond;
exports.isRunningUnderWSL = isRunningUnderWSL;
exports.executeWithLock = executeWithLock;
exports.minimizeEnvironment = minimizeEnvironment;
exports.getOSArch = getOSArch;
exports.getDotnetExecutable = getDotnetExecutable;
exports.EnvironmentVariableIsDefined = EnvironmentVariableIsDefined;
exports.getPathSeparator = getPathSeparator;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const os = require("os");
const CacheTimeConstants_1 = require("../Acquisition/CacheTimeConstants");
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("./CommandExecutor");
const EventStreamNodeIPCMutexWrapper_1 = require("./EventStreamNodeIPCMutexWrapper");
const NodeIPCMutex_1 = require("./NodeIPCMutex");
function loopWithTimeoutOnCond(sampleRatePerMs, durationToWaitBeforeTimeoutMs, conditionToStop, doAfterStop, eventStream, waitEvent) {
    return __awaiter(this, void 0, void 0, function* () {
        for (let i = 0; i < (durationToWaitBeforeTimeoutMs / sampleRatePerMs); i++) {
            if (conditionToStop()) {
                doAfterStop();
                return Promise.resolve();
            }
            eventStream === null || eventStream === void 0 ? void 0 : eventStream.post(waitEvent);
            yield new Promise(waitAndResolve => setTimeout(waitAndResolve, sampleRatePerMs));
        }
        throw new Error(`The promise timed out at ${durationToWaitBeforeTimeoutMs}.`);
    });
}
/**
 * Returns true if the linux agent is running under WSL, else false.
 */
function isRunningUnderWSL(acquisitionContext, utilityContext, executor) {
    return __awaiter(this, void 0, void 0, function* () {
        // See https://github.com/microsoft/WSL/issues/4071 for evidence that we can rely on this behavior.
        var _a;
        (_a = acquisitionContext.eventStream) === null || _a === void 0 ? void 0 : _a.post(new EventStreamEvents_1.DotnetWSLCheckEvent(`Checking if system is WSL. OS: ${os.platform()}`));
        if (os.platform() !== 'linux') {
            return false;
        }
        const command = CommandExecutor_1.CommandExecutor.makeCommand('grep', ['-i', 'Microsoft', '/proc/version']);
        executor !== null && executor !== void 0 ? executor : (executor = new CommandExecutor_1.CommandExecutor(acquisitionContext, utilityContext));
        const commandResult = yield executor.execute(command, {}, false);
        if (!commandResult || !commandResult.stdout) {
            return false;
        }
        return true;
    });
}
function executeWithLock(eventStream, alreadyHoldingLock, lockId, retryTimeMs, timeoutTimeMs, f, ...args) {
    return __awaiter(this, void 0, void 0, function* () {
        // Are we in a mutex-relevant inner function call, that is called by a parent function that already holds the lock?
        // If so, we don't need to acquire the lock again and we also shouldn't release it as the parent function will do that.
        if (alreadyHoldingLock || process.env.VSCODE_DOTNET_RUNTIME_DISABLE_MUTEX === 'true') {
            // eslint-disable-next-line @typescript-eslint/await-thenable
            return f(...(args));
        }
        else {
            const logger = new EventStreamNodeIPCMutexWrapper_1.EventStreamNodeIPCMutexLoggerWrapper(eventStream, lockId);
            const mutex = new NodeIPCMutex_1.NodeIPCMutex(lockId, logger, `The lock may be held by another process or instance of vscode. Try restarting your machine, deleting the lock, and or increasing the timeout time in the extension settings.

Increase your OS path length limit to at least 256 characters.
On Linux, you can set XDG_RUNTIME_DIR to be a writeable directory by your user.

If you still face issues, set VSCODE_DOTNET_RUNTIME_DISABLE_MUTEX=true in the environment.
Report this issue to our vscode-dotnet-runtime GitHub for help.`);
            const result = yield mutex.acquire(() => __awaiter(this, void 0, void 0, function* () {
                // await must be used to make the linter allow f to be async, which it must be.
                // eslint-disable-next-line no-return-await
                return yield f(...(args));
            }), retryTimeMs, timeoutTimeMs, `${lockId}-${crypto.randomUUID()}`);
            return result;
        }
    });
}
const possiblyUsefulUpperCaseEnvVars = new Set([
    'COMMONPROGRAMFILES',
    'COMMONPROGRAMFILES(x86)',
    'PATH',
    'SYSTEMROOT',
    'PROGRAMFILES',
    'POWERSHELL_DISTRIBUTION_CHANNEL',
    'PROCESSOR_IDENTIFIER',
    'PSMODULEPATH',
    'PROCESSOR_ARCHITECTURE',
    'VSCODE_CLI',
    'VSCODE_CODE_CACHE_PATH',
    'VSCODE_HANDLES_UNCAUGHT_ERRORS',
    'RESOLVEDLANGUAGE',
    'VSCODE_PID',
    'WINDIR',
    'DIRCMD',
    'TERM_PROGRAM_VERSION',
    'ALLUSERSPROFILE',
    'COMSPEC',
    'DOTNET_MULTILEVEL_LOOKUP',
    'ELECTRON_RUN_AS_NODE',
    'LANG',
    'HOME',
    'PATHEXT',
    'SHELL',
    'TERM',
    'PWD',
    'BASHOPTS',
    'SHELLOPTS',
    'PS1',
    'PS2',
    'DOTNET_INSTALL_TOOL_UNDER_TEST',
    'VSCODE_DOTNET_GLOBAL_INSTALL_FAKE_PATH',
    'DOTNET_ROOT',
    'DOTNET_ROOT_X86',
    'DOTNET_ROOT_X64',
    'DOTNET_ROOT(x86)',
    'DOTNET_CLI_UI_LANGUAGE',
    'CHCP',
    'DOTNET_NOLOGO',
    'DOTNET_HOST_PATH',
    'DOTNET_ROLL_FORWARD',
    'DOTNET_ROLL_FORWARD_TO_PRERELEASE',
    'DOTNET_ROLL_FORWARD_ON_NO_CANDIDATE_FX',
]);
/*
* @remarks Not intended for telemetry redaction -- PII must be handled correctly elsewhere by a telemetry system.
* This is intended for logging to the console or a log file where we don't want to write out sensitive information, but we do want information that is useful for debugging weird user situations.
*/
function minimizeEnvironment(pathEnv) {
    let pathEnvString = ``;
    for (const key in pathEnv) {
        if (possiblyUsefulUpperCaseEnvVars.has(key.toUpperCase())) {
            pathEnvString += `${key}: ${pathEnv[key]}\n`;
        }
    }
    return pathEnvString;
}
function getOSArch(executor) {
    return __awaiter(this, void 0, void 0, function* () {
        if (os.platform() === 'darwin') {
            const findTrueArchCommand = CommandExecutor_1.CommandExecutor.makeCommand(`uname`, [`-p`]);
            return (yield executor.execute(findTrueArchCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.SYSTEM_INFORMATION_CACHE_DURATION_MS }, false)).stdout.toLowerCase().trim();
        }
        return os.arch();
    });
}
function getDotnetExecutable() {
    return os.platform() === 'win32' ? 'dotnet.exe' : 'dotnet';
}
function EnvironmentVariableIsDefined(variable) {
    // Most of the time this will be 'undefined', so this is the fastest check.
    return variable !== 'undefined' && variable !== null && variable !== '' && variable !== undefined;
}
function getPathSeparator() {
    return os.platform() === 'win32' ? ';' : ':';
}
//# sourceMappingURL=TypescriptUtilities.js.map